import { wishlistPageInitialState, type WishlistPageState } from "./types";
import {
  CHANGE_QUANTITY,
  DELETE_PRODUCT,
  DELETE_WISHLIST,
  LOAD_WISHLIST_DETAILS_POST_PROCESS,
  LOAD_WISHLISTS_START,
  LOAD_WISHLISTS_SUCCESS,
  RENAME_WISHLIST,
  SAVE_COMMENT,
  SAVE_COMMENT_ITEM,
  SELECT_WISHLIST,
  SET_ADD_ALL_CLICKED,
  UPDATE_WISHLIST_ENTRY,
} from "./actionTypes";
import { immutableRemoveKeys } from "../../utils/misc";
import { type ActionType } from "../../redux/types";
import { type FetchWishlistsResponse } from "../api/fetchWishlists";
import { toMapFn } from "../../utils/reducers";
import { type WishlistEntryModel } from "../model";

export const wishlistReducer = (state = wishlistPageInitialState, action: ActionType<unknown>): WishlistPageState => {
  switch (action.type) {
    case LOAD_WISHLISTS_START: {
      return {
        ...state,
        loading: true,
      };
    }

    case LOAD_WISHLISTS_SUCCESS: {
      const data = action.payload as FetchWishlistsResponse;

      return {
        ...state,
        wishlistIds: data.wishlists.map((wl) => wl.id),
        wishlists: toMapFn(data.wishlists, (wl) => wl.id),
        entries: toMapFn(data.wishlistEntries, (entry) => entry.id),
        loading: false,
        loaded: true,
      };
    }

    case LOAD_WISHLIST_DETAILS_POST_PROCESS: {
      const wishlistId = action.payload as string;

      const entries = { ...state.entries };
      const wishlist = state.wishlists[wishlistId];

      // TODO - Move this to wishlist page?
      // const urlParams = new URLSearchParams(window.location.search);
      // const urlExtended = urlParams.get("extended") === "true";
      // if (urlExtended) {
      //   wishlist.entryIds.forEach((id) => {
      //     entries[id] = {
      //       ...entries[id],
      //       showComments: urlExtended,
      //     };
      //   });
      // }

      return {
        ...state,
        entries,
        wishlists: {
          ...state.wishlists,
          [wishlistId]: {
            ...state.wishlists[wishlistId],
            processed: true,
          },
        },
      };
    }

    case SELECT_WISHLIST: {
      const selectedWishlistId = action.payload as string;

      return {
        ...state,
        selectedWishlistId,
      };
    }

    case SAVE_COMMENT: {
      const { comment, wishlistId } = action.payload as {
        wishlistId: string;
        comment: string;
      };

      const wishlistIds = state.wishlistIds.filter((id) => id !== wishlistId);
      wishlistIds.unshift(wishlistId);

      return {
        ...state,
        wishlists: {
          ...state.wishlists,
          [wishlistId]: {
            ...state.wishlists[wishlistId],
            comment,
          },
        },
        wishlistIds,
      };
    }

    case DELETE_WISHLIST: {
      const { wishlistId } = action.payload as { wishlistId: string };

      const wishlist = state.wishlists[wishlistId];
      const entryIds = wishlist.entryIds;
      const entries = immutableRemoveKeys(state.entries, ...entryIds);
      const wishlists = immutableRemoveKeys(state.wishlists, wishlistId);

      return {
        ...state,
        wishlists,
        entries,
        wishlistIds: state.wishlistIds.filter((id) => id !== wishlistId),
      };
    }

    case DELETE_PRODUCT: {
      const { wishlistId, entryId } = action.payload as {
        wishlistId: string;
        entryId: string;
      };

      const entries = immutableRemoveKeys(state.entries, entryId);

      const wishlistIds = state.wishlistIds.filter((id) => id !== wishlistId);
      wishlistIds.unshift(wishlistId);

      return {
        ...state,
        wishlistIds,
        entries,
        wishlists: {
          ...state.wishlists,
          [wishlistId]: {
            ...state.wishlists[wishlistId],
            entryIds: state.wishlists[wishlistId].entryIds.filter((id) => id !== entryId),
          },
        },
      };
    }

    case CHANGE_QUANTITY: {
      const { entryId, newQty, wishlistId } = action.payload as {
        entryId: string;
        newQty: number;
        wishlistId: string;
      };

      const entries = {
        ...state.entries,
        [entryId]: {
          ...state.entries[entryId],
          quantity: newQty,
        },
      };

      const wishlistIds = state.wishlistIds.filter((id) => id !== wishlistId);
      wishlistIds.unshift(wishlistId);

      return {
        ...state,
        entries,
        wishlistIds,
      };
    }

    case UPDATE_WISHLIST_ENTRY: {
      const { updatedEntry, wishlistId } = action.payload as {
        wishlistId: string;
        updatedEntry: WishlistEntryModel;
      };

      const entries = {
        ...state.entries,
        [updatedEntry.id]: {
          ...updatedEntry,
        },
      };

      const wishlistIds = state.wishlistIds.filter((id) => id !== wishlistId);
      wishlistIds.unshift(wishlistId);

      return {
        ...state,
        entries,
        wishlistIds,
      };
    }

    case SAVE_COMMENT_ITEM: {
      const { entryId, comment, wishlistId } = action.payload as {
        entryId: string;
        comment?: string;
        wishlistId: string;
      };

      const entries = {
        ...state.entries,
        [entryId]: {
          ...state.entries[entryId],
          comment: comment ?? "",
        },
      };

      const wishlistIds = state.wishlistIds.filter((id) => id !== wishlistId);
      wishlistIds.unshift(wishlistId);

      return {
        ...state,
        wishlistIds,
        entries: entries,
      };
    }

    case SET_ADD_ALL_CLICKED: {
      const wasClicked = action.payload as boolean;
      return {
        ...state,
        addAllClicked: wasClicked,
      };
    }

    case RENAME_WISHLIST: {
      const { wishlistId, newName } = action.payload as {
        wishlistId: string;
        newName: string;
      };

      const wishlistIds = state.wishlistIds.filter((id) => id !== wishlistId);
      wishlistIds.unshift(wishlistId);

      return {
        ...state,
        wishlists: {
          ...state.wishlists,
          [wishlistId]: {
            ...state.wishlists[wishlistId],
            name: newName,
          },
        },
        wishlistIds,
      };
    }

    default:
      return state;
  }
};
